import localforage from 'localforage';
import _ from 'lodash';

import {
  DEFAULT_STEP_RATIO_BY_PAIR,
  GLOBAL_CONFIG,
  JWT_FOR_AGENT,
  LOCAL_RPC_URL,
  PAIRS_PARTICIPATED,
} from '@/constants/storage';
import { IGlobalConfig, IGlobalConfigStorage } from '@/types/global';

export async function saveGlobalConfigToLocalForage(config: IGlobalConfig): Promise<void> {
  const configInStorage = ((await localforage.getItem(GLOBAL_CONFIG)) || {}) as IGlobalConfigStorage;
  _.set(configInStorage, [config.chainId], config);
  localforage.setItem(GLOBAL_CONFIG, configInStorage);
}

export async function saveParticipatedPairsToLocalForage(
  pairs: { instrumentAddr: string; expiry: number }[],
  userAddr: string,
  chainId: number,
): Promise<void> {
  const storage = ((await localforage.getItem(PAIRS_PARTICIPATED)) || {}) as {
    [chainId: number]: { [userAddr: string]: { instrumentAddr: string; expiry: number }[] };
  };

  _.set(storage, [chainId, userAddr], pairs);
  localforage.setItem(PAIRS_PARTICIPATED, storage);
}

export async function getParticipatedPairsFromLocalForage(
  userAddr: string,
  chainId: number,
): Promise<{ instrumentAddr: string; expiry: number }[]> {
  const storage = ((await localforage.getItem(PAIRS_PARTICIPATED)) || {}) as {
    [chainId: number]: { [userAddr: string]: { instrumentAddr: string; expiry: number }[] };
  };
  const userStorage = _.get(storage, [chainId, userAddr]);
  return Object.values(userStorage || {});
}

export async function saveSingleParticipatedPairsToLocalForage(
  pair: { instrumentAddr: string; expiry: number },
  userAddr: string,
  chainId: number,
): Promise<void> {
  const storage = ((await localforage.getItem(PAIRS_PARTICIPATED)) || {}) as {
    [chainId: number]: { [userAddr: string]: { instrumentAddr: string; expiry: number }[] };
  };

  const pairs: { instrumentAddr: string; expiry: number }[] = _.get(storage, [chainId, userAddr], []);
  if (!pairs.find((p) => p.instrumentAddr === pair.instrumentAddr && p.expiry === pair.expiry)) pairs.push(pair);
  _.set(storage, [chainId, userAddr], pairs);
  localforage.setItem(PAIRS_PARTICIPATED, storage);
}

export async function saveRpcToLocalForage(chainId: number, rpc: string): Promise<void> {
  const rpcInStorage = ((await localforage.getItem(LOCAL_RPC_URL)) || {}) as { [chainId: number]: string };
  if (rpc) {
    _.set(rpcInStorage, [chainId || ''], rpc);
    localforage.setItem(LOCAL_RPC_URL, rpcInStorage);
  } else {
    _.unset(rpcInStorage, [chainId || '']);
    localforage.setItem(LOCAL_RPC_URL, rpcInStorage);
  }
}

export async function getRpcFromLocalForage(
  chainId: number,
): Promise<{ rpc: string; type: 'wss' | 'https' } | undefined> {
  const signatureInStorage = ((await localforage.getItem(LOCAL_RPC_URL)) || {}) as { [chainId: number]: string };
  const rpc: string = _.get(signatureInStorage, [chainId || '']);
  if (rpc && rpc?.length) {
    if (rpc.startsWith('wss')) {
      return {
        rpc,
        type: 'wss',
      };
    }

    return {
      rpc,
      type: 'https',
    };
  }
}

export async function saveDefaultStepRatioToLocalForage(
  pairKey: string,
  stepRatio: number,
  userAddr: string,
  chainId: number,
  type = 'orderBook',
): Promise<void> {
  const storage = ((await localforage.getItem(DEFAULT_STEP_RATIO_BY_PAIR)) || {}) as {
    [chainId: number]: { [userAddr: string]: { [pairKey: string]: { [type: string]: number } } };
  };
  _.set(storage, [chainId, userAddr, pairKey, type], stepRatio);
  localforage.setItem(DEFAULT_STEP_RATIO_BY_PAIR, storage);
}

export async function getDefaultStepRatioFromLocalForage(
  pairKey: string,
  userAddr: string,
  chainId: number,
  type = 'orderBook',
): Promise<number | undefined> {
  const storage = ((await localforage.getItem(DEFAULT_STEP_RATIO_BY_PAIR)) || {}) as {
    [chainId: number]: { [userAddr: string]: { [pairKey: string]: { [type: string]: number } } };
  };
  return _.get(storage, [chainId, userAddr, pairKey, type]);
}

export function getAgentJWTToken(address: string): string | undefined {
  const key = `${JWT_FOR_AGENT}-${address}`;
  const token = localStorage.getItem(key);
  if (token) {
    const tokenData = JSON.parse(token);
    if (tokenData && tokenData.expiry > Date.now()) {
      return tokenData.token;
    }
  }
  return undefined;
}

export function saveAgentJWTToken(address: string, jwtToken: string): void {
  const key = `${JWT_FOR_AGENT}-${address}`;
  const expiryDate = new Date();
  expiryDate.setFullYear(expiryDate.getFullYear() + 1);

  const tokenData = {
    token: jwtToken,
    expiry: expiryDate.getTime(),
  };

  localStorage.setItem(key, JSON.stringify(tokenData));
}
